import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import config from '@/config/config.env'
import { supabase } from '@/services/supabase'

// Module-level token cache to avoid Supabase lock contention
// Updated by AuthProvider's onAuthStateChange before making API calls
let cachedAuthToken: string | null = null

export function setApiAuthToken(token: string | null) {
  cachedAuthToken = token
}

const api = axios.create({
  baseURL: config.urls.baseUrl,
})

// ─── REFRESH MUTEX ───
// Prevents concurrent refresh attempts. With Supabase's token rotation,
// using the same refresh token twice (from parallel 401 retries) will
// invalidate the session entirely.
let isRefreshing = false
let refreshSubscribers: Array<(token: string | null) => void> = []

function subscribeToRefresh(cb: (token: string | null) => void) {
  refreshSubscribers.push(cb)
}

function onRefreshComplete(newToken: string | null) {
  refreshSubscribers.forEach((cb) => cb(newToken))
  refreshSubscribers = []
}

// ─── REQUEST INTERCEPTOR ───
api.interceptors.request.use(
  async (config) => {
    // Use cached token first (avoids getSession lock contention)
    let authToken = cachedAuthToken
    if (!authToken) {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        authToken = session?.access_token || null
      } catch (e) {
        console.warn('API Interceptor: getSession failed, no token available')
      }
    }

    if (authToken) {
      config.headers.Authorization = `Bearer ${authToken}`
    }

    // Portal Context Identification
    config.headers['X-Portal-Context'] = 'candidate'

    return config;
  },
  (error) => {
    return Promise.reject(error)
  }
)

// ─── RESPONSE INTERCEPTOR: 401 auto-retry with token refresh ───
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean
    }

    // ─── 403: Real-time Access Revocation (INDUSTRY BEST) ───
    if (error.response?.status === 403) {
      const data = error.response.data as any;
      if (data?.code === 'ORG_ACCESS_REVOKED') {
        console.warn('Real-time Revocation: Session access restricted.');
        cachedAuthToken = null;
        
        sessionStorage.setItem('access_revocation_active', 'true');

        window.location.href = `/login`;
        return new Promise(() => {}); // Stop further processing
      }
    }

    // Only retry once, only for 401, and only if we have a request config
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true

      // If another request is already refreshing, queue this one
      if (isRefreshing) {
        return new Promise<string | null>((resolve) => {
          subscribeToRefresh((newToken) => {
            if (newToken) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`
              resolve(api(originalRequest) as any)
            } else {
              resolve(null)
            }
          })
        })
      }

      isRefreshing = true

      try {
        // GUARD: Only attempt refresh if a valid refresh token exists.
        // Calling refreshSession() with an empty/missing refresh token triggers
        // "AuthApiError: Invalid Refresh Token: Refresh Token Not Found"
        const { data: { session: currentSession } } = await supabase.auth.getSession()
        
        if (currentSession?.refresh_token) {
          const { data, error: refreshError } =
            await supabase.auth.refreshSession()

          if (!refreshError && data.session) {
            const newToken = data.session.access_token
            setApiAuthToken(newToken)
            isRefreshing = false
            onRefreshComplete(newToken)
            originalRequest.headers.Authorization = `Bearer ${newToken}`
            return api(originalRequest) // retry with fresh token
          }
        } else {
          console.warn('[API] No valid refresh token available — skipping refresh attempt')
        }
      } catch (refreshErr: any) {
        // Suppress the specific "Invalid Refresh Token" error gracefully
        if (refreshErr?.message?.includes('Refresh Token')) {
          console.warn('[API] Refresh token is invalid or expired — redirecting to login')
        } else {
          console.error('Token refresh failed:', refreshErr)
        }
      }

      // Refresh failed — notify all queued requests and redirect
      isRefreshing = false
      onRefreshComplete(null)
      cachedAuthToken = null
      const redirect = encodeURIComponent(window.location.pathname + window.location.search)
      window.location.href = `/login?redirect=${redirect}`
    }

    return Promise.reject(error)
  }
)

export default api
